import type { Partner } from "@expert/shared-types";
import type { JSX } from "react";

import { Flex, Group, Stack, Text } from "@mantine/core";
import { useInterval } from "@mantine/hooks";
import { useState } from "react";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useDetectedCuesStore, useProductOfferStore, useVerifiedCustomerStore } from "../../../../stores";
import { EligibilityStatus } from "../../../../types";
import { DetectedCues } from "../../../product-sale-flow/components/detected-cues/DetectedCues";
import { mapPartnerToDisplayName } from "./OffersView.helpers";

function ShieldIcon() {
    return (
        <svg width="12" height="13.5" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.50002 3.61522V8.69122C1.50005 11.9756 3.52268 14.9207 6.58802 16.0999L7.28202 16.3666C7.74418 16.5443 8.25586 16.5443 8.71802 16.3666L9.41202 16.0999C12.4774 14.9207 14.5 11.9756 14.5 8.69122V3.61522C14.5026 3.22505 14.2761 2.86964 13.9214 2.70722C12.0546 1.89242 10.0368 1.481 8.00002 1.49989C5.96326 1.481 3.94546 1.89242 2.07869 2.70722C1.72392 2.86964 1.49741 3.22505 1.50002 3.61522Z"
                stroke="#A5AAAF"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M5.3335 9.33325L7.3335 10.9999L10.6668 6.33325"
                stroke="#A5AAAF"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function LocationIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M7 7.583a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5Z"
                stroke="#A5AAAF"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M7 12.833S11.667 9.333 11.667 5.833a4.667 4.667 0 0 0-9.334 0c0 3.5 4.667 7 4.667 7Z"
                stroke="#A5AAAF"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function sanitizeRetailName(name: string, partner: Partner) {
    const regex = new RegExp(`^${partner}\\s*`, "i");
    switch (partner) {
        case "att":
            return name.replace(/^(ATT|AT&T)\s*/i, "");
        default:
            return name.replace(regex, "");
    }
}

function CustomerLocation(): JSX.Element | null {
    const { sessionId } = useSalesContext();
    const location = useDetectedCuesStore((state) => state.getLocation(sessionId));
    const [time, setTime] = useState(new Date());
    useInterval(() => setTime(new Date()), 1000, { autoInvoke: true });

    if (!location) return null;

    const parts = [location.city, location.state].filter(Boolean);
    const locationText = parts.join(", ");
    const formattedTime = location.timezone
        ? time
              .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: location.timezone })
              .toLowerCase()
        : undefined;

    return (
        <Flex align="center" gap="xs" data-testid="customer-location">
            <LocationIcon />
            <Group gap="3xs">
                <Text fz="sm" c="dark.0" data-testid="customer-location-text">
                    {locationText}
                </Text>
                {formattedTime && (
                    <Text fz="sm" c="dark.0">
                        {` \u2022 ${formattedTime}`}
                    </Text>
                )}
            </Group>
        </Flex>
    );
}

export function CustomerView({ hideCustomerInsights = false }: { hideCustomerInsights?: boolean }): JSX.Element {
    const { sessionId, partner } = useSalesContext();
    const { asurionAgreements, partnerAgreements } = useVerifiedCustomerStore((state) =>
        state.getVerifiedCustomer(sessionId),
    );
    const hasHomePlus = asurionAgreements.some((product) => product.includes("HOMEPLUS"));
    const hasAppliancePlus = asurionAgreements.some((product) => ["APPLHOMEPLUS35_99"].includes(product));
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));

    const alreadyEnrolled = primaryProducts
        .filter((p) => p.isPrimaryProduct && p.status === EligibilityStatus.Enrolled)
        .map((p) => p.productSku)
        .at(0);

    const { getProductInfo } = useProductOfferStore.getState();
    const showPartnerEnrollment = Boolean(alreadyEnrolled && (partner === "verizon" || partner === "att"));

    return (
        <>
            <Flex direction="column" px={14} pt={14} gap={14}>
                {showPartnerEnrollment && alreadyEnrolled && (
                    <Flex align="center">
                        <ShieldIcon />
                        <Text pl="xs" fz="sm">
                            Enrolled in{" "}
                            <Text component="span" fw={700} fz="sm" style={{ textTransform: "capitalize" }}>
                                {mapPartnerToDisplayName(partner)}{" "}
                                {getProductInfo(sessionId, alreadyEnrolled)?.displayName}
                            </Text>
                        </Text>
                    </Flex>
                )}

                {partnerAgreements.length > 0 &&
                    partnerAgreements.map((product, key) => (
                        <Flex key={key} align="center">
                            <ShieldIcon />
                            <Text pl="xs" fz="sm">
                                Enrolled in{" "}
                                <Text component="span" fw={700} fz="sm" style={{ textTransform: "capitalize" }}>
                                    {mapPartnerToDisplayName(partner)} {sanitizeRetailName(product, partner)}
                                </Text>
                            </Text>
                        </Flex>
                    ))}

                {hasHomePlus && (
                    <Flex align="center">
                        <ShieldIcon />
                        <Text pl="xs" fz="sm">
                            Enrolled in{" "}
                            <Text component="span" fw={700} fz="sm">
                                Home+
                            </Text>
                        </Text>
                    </Flex>
                )}

                {hasAppliancePlus && (
                    <Flex align="center">
                        <ShieldIcon />
                        <Text pl="xs" fz="sm">
                            Enrolled in{" "}
                            <Text component="span" fw={700} fz="sm">
                                Appliance+
                            </Text>
                        </Text>
                    </Flex>
                )}
            </Flex>

            {!hideCustomerInsights && (
                <Stack p="sm" pt="sm" gap="sm">
                    <CustomerLocation />
                    <DetectedCues />
                </Stack>
            )}
        </>
    );
}
